import * as React from 'react'

import { useQuery } from '@apollo/client'
import { Box, Divider, Text } from '@factorialco/deprecated-design-system'
import {
  EmployeeSelectorV2ItemsDocument,
  EmployeeSelectorV2ItemsQuery,
  EmployeeSelectorV2ItemsQueryVariables,
} from 'generated/resources/graphql'
import moment from 'moment'

import TextField from 'modules/core/components/TextField'
import UserAvatar from 'modules/core/components/UserAvatar'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { formatBackendDateRange, i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { useCostCentersByLegalEntity } from 'modules/cost_centers/hooks/useCostCentersByLegalEntity'
import DailyBreakdown from 'modules/expenses/components/expenses/New/PerDiemWizard/components/DailyBreakdown'
import { usePerDiemWizard } from 'modules/expenses/components/expenses/New/PerDiemWizard/form/context'

const formatDateRange = (startDate: Date, endDate: Date): string => {
  // Convert Date objects to backend format strings
  const startDateStr = i18nDateToString(moment(startDate), 'backend')
  const endDateStr = i18nDateToString(moment(endDate), 'backend')

  // Use the existing formatBackendDateRange function
  const formatted = formatBackendDateRange(startDateStr, endDateStr)

  // The formatBackendDateRange uses "—" (em dash) and commas, but we want "–" (en dash) and no commas
  // to match the image format: "12–19 Mar 2026"
  // Replace em dash with en dash and remove commas
  return formatted.replace(/—/g, '–').replace(/,/g, '')
}

const generateDefaultTripName = (
  destinations: Array<{ locationName: string }>,
  startDate: Date | null,
  endDate: Date | null
): string => {
  const destinationNames = destinations
    .filter((dest) => dest.locationName)
    .map((dest) => dest.locationName)
    .join(' - ')

  if (!destinationNames) {
    return ''
  }

  if (!startDate || !endDate) {
    return `${destinationNames} trip`
  }

  const dateRange = formatDateRange(startDate, endDate)
  return `${destinationNames} trip · ${dateRange}`
}

const ReviewAndSubmit = () => {
  const { data, updateData, calculation } = usePerDiemWizard()
  const {
    access: { locale },
    employee,
  } = useCurrentSession()

  const currency = calculation.currency || 'EUR'
  const employeeLegalEntityId = employee?.legalEntity?.id

  // Fetch cost centers
  const { costCenters } = useCostCentersByLegalEntity({
    legalEntityId: employeeLegalEntityId,
    skip: !employeeLegalEntityId,
  })

  // Find the cost center name by ID
  const costCenterName = React.useMemo(() => {
    const costCenterId = data.costCenterAssignments[0]?.costCenterId
    if (!costCenterId) return null
    const costCenter = costCenters.find((cc) => cc.id === costCenterId)
    return costCenter?.name || null
  }, [data.costCenterAssignments, costCenters])

  const defaultTripName = React.useMemo(
    () => generateDefaultTripName(data.destinations, data.startDate, data.endDate),
    [data.destinations, data.startDate, data.endDate]
  )

  const tripNameValue = data.tripName || defaultTripName

  // Fetch requester employee data
  const { data: requesterData } = useQuery<
    EmployeeSelectorV2ItemsQuery,
    EmployeeSelectorV2ItemsQueryVariables
  >(EmployeeSelectorV2ItemsDocument, {
    variables: { ids: data.employeeId ? [data.employeeId] : [] },
    skip: !data.employeeId,
  })

  const requesterEmployee = requesterData?.employees?.employeesConnection?.nodes?.[0]?.employeeName

  // Calculate trip duration
  const tripDuration = React.useMemo(() => {
    if (!data.startDate || !data.endDate) {
      return null
    }
    const start = moment(data.startDate)
    const end = moment(data.endDate)
    const days = end.diff(start, 'days') + 1 // +1 to include both start and end days
    const nights = days - 1
    return { days, nights }
  }, [data.startDate, data.endDate])

  // Count company-paid deductions
  const companyPaidDeductionsCount = React.useMemo(() => {
    return data.dayExclusions.reduce((count, exclusion) => {
      const exclusionCount =
        (exclusion.breakfastProvided ? 1 : 0) +
        (exclusion.lunchProvided ? 1 : 0) +
        (exclusion.dinnerProvided ? 1 : 0) +
        (exclusion.accommodationProvided ? 1 : 0)
      return count + exclusionCount
    }, 0)
  }, [data.dayExclusions])

  // Format destination names with "roundtrip" if applicable
  const destinationText = React.useMemo(() => {
    const destinationNames = data.destinations
      .filter((dest) => dest.locationName)
      .map((dest) => dest.locationName)
      .join(' - ')

    if (data.destinations.length > 1) {
      return `${destinationNames} roundtrip`
    }
    return destinationNames
  }, [data.destinations])

  // Format date range with duration
  const dateRangeText = React.useMemo(() => {
    if (!data.startDate || !data.endDate || !tripDuration) {
      return null
    }
    const dateRange = formatDateRange(data.startDate, data.endDate)
    return `${dateRange} (${tripDuration.days} day${tripDuration.days !== 1 ? 's' : ''} · ${
      tripDuration.nights
    } night${tripDuration.nights !== 1 ? 's' : ''})`
  }, [data.startDate, data.endDate, tripDuration])

  return (
    <Box flexDirection="column" gap="s8">
      <Text.Normal size="300" weight="semibold" color="grey1000">
        {i18n.t('expenses_v2.forms.germany_per_diem_modal.steps.step3.title')}
      </Text.Normal>

      <Text.Normal size="200" weight="medium" color="grey700"></Text.Normal>

      <Box flexDirection="column" gap="s4" paddingBottom="s16">
        <TextField
          label="Trip name"
          placeholder="Enter trip name"
          value={tripNameValue}
          onChange={(value?: string | null) => updateData({ tripName: value || undefined })}
        />
        {!data.tripName && defaultTripName && (
          <Text.Normal size="200" weight="normal" color="grey700">
            We suggested a name from your route. You can change it.
          </Text.Normal>
        )}
      </Box>

      {requesterEmployee && (
        <Box flexDirection="column" gap="s4" paddingBottom="s8">
          <Text.Normal size="200" weight="normal" color="grey700">
            Owner
          </Text.Normal>
          <Box flexDirection="row" alignItems="center" gap="s4">
            <UserAvatar
              size="s40"
              user={{
                avatar: requesterEmployee.access?.avatar || null,
                fullName: requesterEmployee.fullName || '',
              }}
            />
            <Text.Normal size="200" weight="medium" color="grey1000">
              {requesterEmployee.fullName}
            </Text.Normal>
          </Box>
        </Box>
      )}
      <Divider vertical={false} />

      <Box flexDirection="column" gap="s4" paddingY="s16">
        <Text.Normal size="200" weight="normal" color="grey700">
          Details
        </Text.Normal>

        <Box paddingLeft="s8">
          <ul>
            {destinationText && (
              <li style={{ display: 'list-item', listStyle: 'inside' }}>
                <Box inline>
                  <Text.Normal size="200" weight="medium" color="grey1000">
                    {destinationText}
                  </Text.Normal>
                </Box>
              </li>
            )}
            {dateRangeText && (
              <li style={{ display: 'list-item', listStyle: 'inside' }}>
                <Box inline>
                  <Text.Normal size="200" weight="medium" color="grey1000">
                    {dateRangeText}
                  </Text.Normal>
                </Box>
              </li>
            )}
            {companyPaidDeductionsCount > 0 && (
              <li style={{ display: 'list-item', listStyle: 'inside' }}>
                <Box inline>
                  <Text.Normal size="200" weight="medium" color="grey1000">
                    {companyPaidDeductionsCount} company-paid deduction
                    {companyPaidDeductionsCount !== 1 ? 's' : ''}
                  </Text.Normal>
                </Box>
              </li>
            )}
          </ul>
        </Box>
      </Box>

      <Divider vertical={false} />

      <Box flexDirection="column" gap="s4" paddingY="s16">
        <Text.Normal size="200" weight="normal" color="grey700">
          Per diem rules
        </Text.Normal>

        <Text.Normal size="200" weight="medium" color="grey1000">
          German per diem rules applied
        </Text.Normal>
      </Box>

      <Divider vertical={false} />

      <Box flexDirection="column" gap="s4" paddingY="s16">
        <Text.Normal size="200" weight="normal" color="grey700">
          Category
        </Text.Normal>
        <Text.Normal size="200" weight="medium" color="grey1000">
          Travel
        </Text.Normal>
      </Box>

      <Divider vertical={false} />

      <Box flexDirection="column" gap="s4" paddingY="s16">
        <Text.Normal size="200" weight="normal" color="grey700">
          Cost centers
        </Text.Normal>
        <Text.Normal size="200" weight="medium" color="grey1000">
          {costCenterName || data.costCenterAssignments[0]?.costCenterId?.toString() || '-'}
        </Text.Normal>
      </Box>

      <DailyBreakdown
        days={calculation.days ?? null}
        totalAmountCents={calculation.totalAmountCents}
        currency={currency}
        locale={locale}
      />
    </Box>
  )
}

export default ReviewAndSubmit
